<template lang="html">
  <chart
    :type="type"
    :image="image"
    :datasets="linesData"
    :options="computedOptions"
    :width="width"
    :height="height"
    @click="click"
    @ready="ready"
    @clickchart="clickchart" />
</template>

<script>
/**
* Chart component for line charts.
*/
import Color from 'color'
import merge from 'deepmerge'
import { arrayMerge } from '@/utils/arrays'
import colors from './colors'
import Chart from './Chart'
export default {
  name: 'ChartLine',
  components: {
    Chart
  },
  props: {
    /**
     * Datasets
     */
    datasets: {
      type: Array,
      required: true
    },
    /**
     * To display the chart as line or bar
     */
    bar: {
      type: Boolean
    },
    /**
     * To display the chart horizontally
     */
    horizontal: {
      type: Boolean
    },
    /**
     * To display area of color under the lines or inside the bars
     */
    fill: {
      type: Boolean,
      default: false
    },
    /**
     * Overwrite or merge with default options
     */
    options: {
      type: Object
    },
    /**
     * Canvas width (responsive so used as a ratio)
     */
    width: {
      type: Number,
      default: 400
    },
    /**
     * Canvas height (responsive so used as a ratio)
     */
    height: {
      type: Number,
      default: 250
    },
    /**
     * Array of abscissa values
     */
    labels: {
      type: Array
    },
    /**
     * Time is defined an object with one property: 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year' with a value of a format to display with momentjs cf.: https://momentjs.com/docs/#/displaying/format/
     */
    time: {
      type: Object
    },
    /**
     * If true, chart is displayed as a png, not a canvas (useful for pdf)
     */
    image: {
      type: Boolean,
      default: false
    },
    /**
     * Array of colors applied to datasets
     */
    colors: {
      type: Array,
      default: () => colors
    }
  },
  computed: {
    type () {
      // return this.line ? 'LineWithLine' : this.bar ? 'bar' : 'line'
      return this.horizontal ? 'horizontalBar' : this.bar ? 'bar' : 'line'
    },
    computedOptions () {
      let opts = {
        scales: {
          yAxes: [
            {
              display: true,
              ticks: {
                beginAtZero: true
              }
            }
          ]
        },
        tooltips: {
          mode: 'index',
          intersect: false
        },
        elements: {
          line: {
            tension: 0 // disables bezier curves
          }
        },
        // animation: {
        //   duration: 0 // general animation time
        // },
        hover: {
          mode: 'nearest' // ,
          // intersect: true
          // animationDuration: 0 // duration of animations when hovering an item
        } // ,
        // responsiveAnimationDuration: 0 // animation duration after a resize
      }
      if (this.time) {
        const unit = Object.keys(this.time)[0]
        opts.scales.xAxes = [
          {
            display: true,
            type: 'time',
            time: {
              unit,
              isoWeekday: true,
              tooltipFormat: this.time[unit],
              displayFormats: this.time
            }
          }
        ]
        if (this.bar) {
          opts.scales.xAxes[0].offset = true
        }
        if (this.timelabels) {
          const datalabels = this.datasets.map(d => {
            const data = d.data
            return data.map(v => v.t)
          })
          opts.scales.xAxes.time.max = datalabels[datalabels.length - 1]
          opts.scales.xAxes.time.min = datalabels[0]
        }
      } else if (this.bar) {
        opts.scales.xAxes = [{ offset: true }]
      }
      return this.options ? merge(opts, this.options, { arrayMerge }) : opts
    },
    linesData () {
      let ld = {
        datasets: this.datasets.map((d, i) => {
          const lineColor = this.colors[i]
          const bgColor = Color(lineColor).rgb().string() // .alpha(0.15).string()
          d.borderColor = d.borderColor || lineColor
          d.borderWidth = d.borderWidth || 1
          d.backgroundColor = d.backgroundColor || bgColor
          d.fill = this.fill
          return d
        })
      }
      if (this.time) {
        const datalabels = this.datasets.map(d => {
          const data = d.data
          return data.map(v => v.t)
        })
        const timeLabels = [...new Set([].concat.apply([], datalabels))]
        ld.labels = timeLabels.map(d => this.moment(d))
      } else if (this.labels) {
        ld.labels = this.labels
      }
      return ld
    }
  },
  methods: {
    click (data) {
      /**
       * Returns { type: 'data', label, value, dataset } or { type: 'dataset', dataset }
       *
       * @event click
       * @type {object}
       */
      this.$emit('click', data)
    },
    ready () {
      /**
       * Emited when animations are complete, useful to know when png version is available in the DOM
       *
       * @event ready
       * @type {event}
       */
      this.$emit('ready')
    },
    clickchart (data) {
      this.$emit('clickchart', data)
    }
  }
}
</script>

<style lang="scss">
// .chart-line {
//   padding: 20px;
// }
</style>
